import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {number} */
  #variantPage = 1;

  #variantPaginationObserver = new MutationObserver((mutations) => {
    const currentGrid = this.querySelector('[ref="grid"]');
    const gridChanged = mutations.some(
      (mutation) =>
        mutation.target === currentGrid ||
        [...mutation.addedNodes, ...mutation.removedNodes].some(
          (node) => node instanceof Element && (node.matches('[ref="grid"]') || node.querySelector('[ref="grid"]'))
        )
    );

    if (gridChanged) queueMicrotask(() => this.#setupVariantPagination());
  });

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    window.addEventListener('popstate', this.#handleVariantPaginationPopState);
    this.#variantPaginationObserver.observe(this, { childList: true, subtree: true });
    this.#setupVariantPagination();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    window.removeEventListener('popstate', this.#handleVariantPaginationPopState);
    this.#variantPaginationObserver.disconnect();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setupVariantPagination();
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  /**
   * Changes the visible page when variant cards are paginated in the browser.
   *
   * @param {MouseEvent} event
   */
  updateVariantPage(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const page = Number(event.target.dataset.variantPage);
    if (!Number.isInteger(page) || page < 1) return;

    event.preventDefault();
    this.#showVariantPage(page);

    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    if (this.#variantPage === 1) {
      url.searchParams.delete('variant_page');
    } else {
      url.searchParams.set('variant_page', this.#variantPage.toString());
    }
    history.pushState('', '', url);

    const { grid } = this.refs;
    grid?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  #setupVariantPagination() {
    const { grid, variantPagination } = this.refs;
    if (!(grid instanceof HTMLElement) || !(variantPagination instanceof HTMLElement)) return;
    if (!grid.hasAttribute('data-variant-pagination')) return;

    const requestedPage = Number(new URL(window.location.href).searchParams.get('variant_page')) || 1;
    this.#showVariantPage(requestedPage);
    grid.setAttribute('data-variant-pagination-ready', '');
  }

  /** @param {number} requestedPage */
  #showVariantPage(requestedPage) {
    const { cards, grid, variantPagination, variantPaginationList } = this.refs;
    if (!(grid instanceof HTMLElement)) return;
    if (!(variantPagination instanceof HTMLElement) || !(variantPaginationList instanceof HTMLUListElement)) return;

    const cardElements = Array.isArray(cards) ? cards.filter((card) => card instanceof HTMLElement) : [];
    const pageSize = Number(grid.dataset.variantPageSize);
    if (!Number.isInteger(pageSize) || pageSize < 1) return;

    const pageCount = Math.max(1, Math.ceil(cardElements.length / pageSize));
    this.#variantPage = Math.min(Math.max(1, Math.trunc(requestedPage)), pageCount);
    const firstVisibleIndex = (this.#variantPage - 1) * pageSize;
    const lastVisibleIndex = firstVisibleIndex + pageSize;

    cardElements.forEach((card, index) => {
      card.hidden = index < firstVisibleIndex || index >= lastVisibleIndex;
    });

    this.#updateVariantResultCounts(cardElements.length);
    variantPagination.hidden = pageCount <= 1;
    variantPagination.dataset.currentPage = this.#variantPage.toString();
    variantPaginationList.replaceChildren(...this.#buildVariantPagination(pageCount, variantPagination));
  }

  /**
   * @param {number} pageCount
   * @param {HTMLElement} pagination
   * @returns {HTMLLIElement[]}
   */
  #buildVariantPagination(pageCount, pagination) {
    const items = [];
    items.push(this.#createVariantPaginationArrow('previous', this.#variantPage - 1, this.#variantPage === 1, pagination));

    for (const token of this.#getVariantPaginationTokens(pageCount)) {
      if (token === 'ellipsis') {
        const item = document.createElement('li');
        item.className = 'pagination__item pagination__item--gap';
        const gap = document.createElement('span');
        gap.className = 'pagination__link pagination__link--gap';
        gap.setAttribute('aria-hidden', 'true');
        gap.innerHTML = '&hellip;';
        item.append(gap);
        items.push(item);
        continue;
      }

      const item = document.createElement('li');
      item.className = 'pagination__item';
      const pageLabel = (pagination.dataset.pageLabel || 'Page __PAGE__').replace('__PAGE__', token.toString());

      if (token === this.#variantPage) {
        const current = document.createElement('span');
        current.className = 'pagination__link pagination__link--page pagination__link--current';
        current.setAttribute('aria-current', 'page');
        current.textContent = token.toString();
        item.append(current);
      } else {
        item.append(this.#createVariantPaginationLink(token, pageLabel, token.toString()));
      }
      items.push(item);
    }

    items.push(
      this.#createVariantPaginationArrow('next', this.#variantPage + 1, this.#variantPage === pageCount, pagination)
    );
    return items;
  }

  /** @param {number} pageCount */
  #getVariantPaginationTokens(pageCount) {
    const pages = new Set([1, pageCount, this.#variantPage - 1, this.#variantPage, this.#variantPage + 1]);
    const visiblePages = [...pages].filter((page) => page >= 1 && page <= pageCount).sort((a, b) => a - b);
    const tokens = [];

    visiblePages.forEach((page, index) => {
      if (index > 0 && page - visiblePages[index - 1] > 1) tokens.push('ellipsis');
      tokens.push(page);
    });
    return tokens;
  }

  /**
   * @param {'previous' | 'next'} direction
   * @param {number} page
   * @param {boolean} disabled
   * @param {HTMLElement} pagination
   */
  #createVariantPaginationArrow(direction, page, disabled, pagination) {
    const item = document.createElement('li');
    item.className = 'pagination__item';
    const label = pagination.dataset[`${direction}Label`] || direction;

    if (disabled) {
      const arrow = document.createElement('span');
      arrow.className = 'pagination__link pagination__link--arrow pagination__link--disabled';
      arrow.setAttribute('aria-label', label);
      arrow.setAttribute('aria-disabled', 'true');
      arrow.textContent = direction === 'previous' ? '‹' : '›';
      item.append(arrow);
    } else {
      item.append(this.#createVariantPaginationLink(page, label, direction === 'previous' ? '‹' : '›', true));
    }
    return item;
  }

  /**
   * @param {number} page
   * @param {string} label
   * @param {string} text
   * @param {boolean} [arrow]
   */
  #createVariantPaginationLink(page, label, text, arrow = false) {
    const link = document.createElement('a');
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    if (page === 1) url.searchParams.delete('variant_page');
    else url.searchParams.set('variant_page', page.toString());

    link.href = url.toString();
    link.className = `pagination__link ${arrow ? 'pagination__link--arrow' : 'pagination__link--page'}`;
    link.setAttribute('aria-label', label);
    link.setAttribute('on:click', 'updateVariantPage');
    link.dataset.variantPage = page.toString();
    link.textContent = text;
    return link;
  }

  /** @param {number} count */
  #updateVariantResultCounts(count) {
    for (const counter of this.querySelectorAll('[data-variant-results-count]')) {
      if (!(counter instanceof HTMLElement)) continue;

      const template = count === 1 ? counter.dataset.countOne : counter.dataset.countOther;
      if (!template) continue;

      counter.textContent = template.replace('__COUNT__', count.toString());
    }
  }

  #handleVariantPaginationPopState = () => {
    const page = Number(new URL(window.location.href).searchParams.get('variant_page')) || 1;
    this.#showVariantPage(page);
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
